import React, { useMemo } from 'react';
import type { BomPart } from '../../services/industrializationService';
import { CostConfirmBar, GenerateButton, ToVerifyBadge, type StepProps } from './shared';

// ── BomRow: renders one part + its children recursively ──────────────────────

interface BomRowProps {
  part: BomPart;
  depth: number;
  childMap: Map<string, BomPart[]>;
}

const BomRow: React.FC<BomRowProps> = ({ part, depth, childMap }) => (
  <>
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="py-1 pr-2 text-xs" style={{ paddingLeft: `${8 + depth * 16}px` }}>
        {part.name}
        {part.to_verify && <ToVerifyBadge />}
      </td>
      <td className="py-1 pr-2 text-xs text-slate-500">{part.quantity ?? '—'}</td>
      <td className="py-1 text-xs text-slate-500">{part.description}</td>
    </tr>
    {(childMap.get(part.id) ?? []).map(child => (
      <BomRow key={child.id} part={child} depth={depth + 1} childMap={childMap} />
    ))}
  </>
);

// ── BomStep ───────────────────────────────────────────────────────────────────

const BomStep: React.FC<StepProps> = ({
  dossier,
  isRunning,
  costEstimate,
  showCostConfirm,
  onRequestGenerate,
  onConfirmGenerate,
  onCancelGenerate,
}) => {
  const bom: BomPart[] = dossier?.bom ?? [];

  // Build id→children map once; O(n) vs repeated filter calls
  const { roots, childMap } = useMemo(() => {
    const knownIds = new Set(bom.map(p => p.id));
    const map = new Map<string, BomPart[]>();
    const rootList: BomPart[] = [];

    for (const part of bom) {
      if (!part.parent_id || !knownIds.has(part.parent_id)) {
        rootList.push(part);
      } else {
        const siblings = map.get(part.parent_id) ?? [];
        siblings.push(part);
        map.set(part.parent_id, siblings);
      }
    }
    return { roots: rootList, childMap: map };
  }, [bom]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Step 2 — Bill of Materials</h3>
        {showCostConfirm ? (
          <CostConfirmBar
            costEstimate={costEstimate}
            confirmLabel="Generate BoM"
            onConfirm={onConfirmGenerate}
            onCancel={onCancelGenerate}
          />
        ) : (
          <GenerateButton label="Generate BoM" isRunning={isRunning} onRequest={onRequestGenerate} />
        )}
      </div>

      {bom.length > 0 ? (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-100">
              <th className="text-left py-1 px-2 font-medium text-slate-600">Part / Assembly</th>
              <th className="text-left py-1 pr-2 font-medium text-slate-600">Qty</th>
              <th className="text-left py-1 font-medium text-slate-600">Description</th>
            </tr>
          </thead>
          <tbody>
            {roots.map(p => (
              <BomRow key={p.id} part={p} depth={0} childMap={childMap} />
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-xs text-slate-400 italic">
          {isRunning
            ? 'Generating BoM…'
            : 'No BoM yet. Generate the Brief first, then click "Generate BoM".'}
        </p>
      )}
    </div>
  );
};

export default BomStep;
